import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchBar = ({ mobile = false, onSelect }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (query.length >= 2) {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const fetchSuggestions = async (q) => {
    try {
      const { data } = await axios.get(`${API}/search/suggestions?q=${encodeURIComponent(q)}`);
      setSuggestions(data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
      setShowDropdown(false);
      onSelect && onSelect();
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <form onSubmit={handleSubmit}>
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Rechercher des produits, marques, références..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            className="w-full pl-4 pr-12 py-2.5 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
            data-testid={mobile ? "mobile-search-input" : "search-input"}
          />
          <button
            type="submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-brand-orange text-white p-2 rounded-sm hover:bg-orange-600"
            data-testid="search-button"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      </form>

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-sm shadow-lg z-50 max-h-96 overflow-auto">
          {suggestions.map((item) => (
            <Link
              key={item.id}
              to={`/products/${item.id}`}
              onClick={() => {
                setShowDropdown(false);
                setQuery('');
                onSelect && onSelect();
              }}
              className="flex items-center gap-3 p-3 hover:bg-surface border-b border-border last:border-b-0"
              data-testid={`suggestion-${item.id}`}
            >
              <img
                src={item.images?.[0]}
                alt={item.name}
                className="w-12 h-12 object-cover rounded-sm flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{item.category?.replace(/-/g, ' ')}</p>
              </div>
              <span className="font-bold text-brand-orange text-sm">
                {item.price.toFixed(2)} €
              </span>
            </Link>
          ))}
          <button
            onClick={handleSubmit}
            className="block w-full text-center py-2.5 bg-surface hover:bg-muted text-sm font-medium text-brand-orange border-t border-border"
          >
            Voir tous les résultats pour "{query}"
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
